import { Heading } from '@radix-ui/themes';
import { CommonDialog } from 'components/common/dialog';
import { CommonFormDialog } from 'components/common/dialog/form-dialog';
import { ActionSelect } from 'components/layout/action-select';
import { EB } from 'components/layout/error-boundary';
import { AccountDetails } from 'components/main/routes/accounts/details';
import { AccountForm } from 'components/main/routes/accounts/form';
import { CommonTable } from 'components/table';
import { AuthContext } from 'contexts/auth';
import { DialogType } from 'enums/dialog';
import { DialogState } from 'interfaces/dialog/state';
import { BaseDoc } from 'lib/interfaces/database/_base';
import { AccountWhole } from 'lib/interfaces/database/account';
import { SortUtil } from 'lib/utilities/sort';
import { StringUtil } from 'lib/utilities/string';
import { useContext, useEffect, useState } from 'react';
import useAccountsStore from 'stores/accounts';

export const Accounts = () => {
  const { data, create, load, update } = useAccountsStore();

  const { perms } = useContext(AuthContext);
  const { accounts: crud } = perms;

  useEffect(() => {
    if (crud?.read) {
      load();
    }
  }, [crud?.read, load]);

  const [dialog, setDialog] = useState<DialogState>();

  return (
    <EB>
      <Heading>Accounts</Heading>
      <CommonTable
        defaultSort={{
          iCol: 1,
          asc: false,
        }}
        toolbarActions={[
          {
            hidden: !crud?.create,
            label: 'Create',
            onClick: () =>
              setDialog({
                key: Date.now(),
                type: DialogType.Create,
                data: {},
              }),
          },
        ]}
        columns={[
          {
            header: 'Actions',
            rowFn: (item: AccountWhole) => (
              <ActionSelect
                options={[
                  {
                    // anyone who can update an account doesn't need the view-only dialog and vice-versa
                    hidden: crud?.update,
                    label: 'Details',
                    value: DialogType.Inspect,
                  },
                  {
                    hidden: !crud?.update,
                    label: 'Update',
                    value: DialogType.Update,
                  },
                ]}
                onValueChange={(v) => {
                  // new value never gets set but it does trigger the effect below
                  switch (v) {
                    case DialogType.Inspect:
                    case DialogType.Update: {
                      setDialog({
                        key: Date.now(),
                        type: v,
                        data: item,
                      });
                      break;
                    }

                    default: {
                      break;
                    }
                  }
                }}
              />
            ),
          },
          {
            header: 'Created',
            rowFn: (item: AccountWhole) =>
              StringUtil.printDateTime(item.createdAt),
            sortFn: (a: AccountWhole, b: AccountWhole) =>
              SortUtil.dates(a.createdAt, b.createdAt),
          },
          {
            header: 'Investor',
            rowFn: (item: AccountWhole) => item._investor?.code,
          },
          {
            header: 'Type',
            rowFn: (item: AccountWhole) => item.type,
            sortFn: (a: AccountWhole, b: AccountWhole) =>
              SortUtil.strings(a.type, b.type),
          },
          {
            header: 'Identifier',
            rowFn: (item: AccountWhole) => '*' + item.privateIdentifier,
            sortFn: (a: AccountWhole, b: AccountWhole) =>
              SortUtil.strings(a.privateIdentifier, b.privateIdentifier),
          },
        ]}
        data={data}
      />

      {dialog?.type === DialogType.Create && (
        <CommonFormDialog
          key={dialog.key}
          title="Create"
          children={<AccountForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: (item) => create(item),
          }}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.type === DialogType.Inspect && (
        <CommonDialog
          key={dialog.key}
          title="Account Details"
          children={<AccountDetails account={dialog.data} />}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.type === DialogType.Update && (
        <CommonFormDialog
          key={dialog.key}
          title="Update"
          children={<AccountForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: (item) => update((dialog.data as BaseDoc).id, item),
          }}
          onClose={() => setDialog(undefined)}
        />
      )}
    </EB>
  );
};
